/**
 * Queue Tab Component
 * Shows dual extraction queue and system task queue statistics with dead letter queue management
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import {
  Inbox,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  AlertCircle,
  Loader2,
  RotateCcw,
  Server,
} from "lucide-react";
import { useI18n } from "@/i18n";
import { monitoringApi } from "@/lib/api/monitoring";
import type {
  QueueStats,
  DeadLetterTask,
  SystemTaskQueueStats,
  SystemTaskDeadLetterItem,
} from "@/types/monitoring";

interface QueueTabProps {
  autoRefreshEnabled?: boolean;
  refreshInterval?: number;
}

/**
 * Format timestamp to readable date string
 */
function formatTimestamp(timestamp: number | null): string {
  if (!timestamp) return "-";
  return new Date(timestamp * 1000).toLocaleString();
}

/**
 * Format percentage
 */
function formatPercent(value: number): string {
  return (value * 100).toFixed(1) + "%";
}

export function QueueTab({
  autoRefreshEnabled = true,
  refreshInterval = 10000,
}: QueueTabProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.queue || {
    title: "提取队列",
    status: "队列状态",
    running: "运行中",
    stopped: "已停止",
    queueSize: "队列大小",
    processed: "已处理",
    success: "成功",
    failed: "失败",
    merged: "已合并",
    savedCalls: "节省调用",
    mergeRate: "合并率",
    costSaving: "成本节省率",
    deadLetter: "死信队列",
    deadLetterDesc: "重试失败后的任务会进入死信队列，可手动重试或清理",
    retryAll: "全部重试",
    clearAll: "清空队列",
    retry: "重试",
    noTasks: "暂无失败任务",
    taskId: "任务ID",
    userId: "用户ID",
    memoryType: "类型",
    error: "错误",
    failedAt: "失败时间",
    retryCount: "重试次数",
    confirmClear: "确定要清空死信队列吗？此操作不可恢复。",
    confirmRetryAll: "确定要重试所有失败任务吗？",
    clearSuccess: "已清空 {count} 个任务",
    retrySuccess: "已重试 {count} 个任务",
    resetStats: "重置统计",
    confirmReset: "确定要重置队列统计数据吗？",
    resetSuccess: "统计数据已重置",
  };

  const st = messages.monitoring.systemTaskQueue || {
    title: "系统任务队列",
    queueSize: "队列大小",
    deadLetterSize: "死信数量",
    maxRetries: "最大重试次数",
    retryDelay: "重试延迟",
    jobTimeout: "任务超时",
    deadLetter: "系统任务死信队列",
    deadLetterDesc: "系统任务（如定时清理）失败后进入此队列",
    taskType: "任务类型",
    scheduledBy: "调度来源",
    scheduleId: "计划ID",
    config: "配置",
    lastError: "最后错误",
    finalError: "最终错误",
    failedAt: "失败时间",
    retryCount: "重试次数",
    noTasks: "暂无失败任务",
    confirmClear: "确定要清空系统任务死信队列吗？",
    confirmRetryAll: "确定要重试所有失败的系统任务吗？",
    clearSuccess: "已清空 {count} 个系统任务",
    retrySuccess: "已重试 {count} 个系统任务",
  };

  const queryClient = useQueryClient();
  const [selectedTask, setSelectedTask] = useState<number | null>(null);
  const [selectedSystemTask, setSelectedSystemTask] = useState<number | null>(
    null
  );

  // Fetch dual extraction queue stats
  const {
    data: stats,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery<QueueStats>({
    queryKey: ["queueStats"],
    queryFn: monitoringApi.getQueueStats,
    refetchInterval: autoRefreshEnabled ? refreshInterval : false,
  });

  // Fetch system task queue stats
  const {
    data: systemTaskStats,
    isLoading: isLoadingSystemTask,
    refetch: refetchSystemTask,
    isFetching: isFetchingSystemTask,
  } = useQuery<SystemTaskQueueStats>({
    queryKey: ["systemTaskQueueStats"],
    queryFn: monitoringApi.getSystemTaskQueueStats,
    refetchInterval: autoRefreshEnabled ? refreshInterval : false,
  });

  // Fetch system task dead letter queue
  const { data: systemTaskDeadLetter } = useQuery({
    queryKey: ["systemTaskDeadLetterQueue"],
    queryFn: monitoringApi.getSystemTaskDeadLetterQueue,
    refetchInterval: autoRefreshEnabled ? refreshInterval : false,
    enabled: (systemTaskStats?.dead_letter_size ?? 0) > 0,
  });

  // Retry single task mutation
  const retryTaskMutation = useMutation({
    mutationFn: (taskIndex: number) =>
      monitoringApi.retryDeadLetterTask(taskIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queueStats"] });
      setSelectedTask(null);
    },
  });

  // Retry all tasks mutation
  const retryAllMutation = useMutation({
    mutationFn: monitoringApi.retryAllDeadLetterTasks,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["queueStats"] });
      if (result.retried) {
        alert(t.retrySuccess.replace("{count}", String(result.retried)));
      }
    },
  });

  // Clear dead letter queue mutation
  const clearMutation = useMutation({
    mutationFn: monitoringApi.clearDeadLetterQueue,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["queueStats"] });
      if (result.deleted) {
        alert(t.clearSuccess.replace("{count}", String(result.deleted)));
      }
    },
  });

  // Reset stats mutation
  const resetStatsMutation = useMutation({
    mutationFn: monitoringApi.resetQueueStats,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queueStats"] });
      alert(t.resetSuccess);
    },
  });

  // System Task Queue Mutations
  const retrySystemTaskMutation = useMutation({
    mutationFn: (taskIndex: number) =>
      monitoringApi.retrySystemTaskDeadLetterTask(taskIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["systemTaskQueueStats"] });
      queryClient.invalidateQueries({
        queryKey: ["systemTaskDeadLetterQueue"],
      });
      setSelectedSystemTask(null);
    },
  });

  const retryAllSystemTasksMutation = useMutation({
    mutationFn: monitoringApi.retryAllSystemTaskDeadLetterTasks,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["systemTaskQueueStats"] });
      queryClient.invalidateQueries({
        queryKey: ["systemTaskDeadLetterQueue"],
      });
      if (result.retried) {
        alert(st.retrySuccess.replace("{count}", String(result.retried)));
      }
    },
  });

  const clearSystemTaskDeadLetterMutation = useMutation({
    mutationFn: monitoringApi.clearSystemTaskDeadLetterQueue,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["systemTaskQueueStats"] });
      queryClient.invalidateQueries({
        queryKey: ["systemTaskDeadLetterQueue"],
      });
      if (result.deleted) {
        alert(st.clearSuccess.replace("{count}", String(result.deleted)));
      }
    },
  });

  const handleRetryAll = () => {
    if (confirm(t.confirmRetryAll)) {
      retryAllMutation.mutate();
    }
  };

  const handleClearAll = () => {
    if (confirm(t.confirmClear)) {
      clearMutation.mutate();
    }
  };

  const handleResetStats = () => {
    if (confirm(t.confirmReset)) {
      resetStatsMutation.mutate();
    }
  };

  const handleRetryTask = (index: number) => {
    retryTaskMutation.mutate(index);
  };

  // System Task Queue Handlers
  const handleRetryAllSystemTasks = () => {
    if (confirm(st.confirmRetryAll)) {
      retryAllSystemTasksMutation.mutate();
    }
  };

  const handleClearAllSystemTasks = () => {
    if (confirm(st.confirmClear)) {
      clearSystemTaskDeadLetterMutation.mutate();
    }
  };

  const handleRetrySystemTask = (index: number) => {
    setSelectedSystemTask(index);
    retrySystemTaskMutation.mutate(index);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-lg font-semibold text-red-900 mb-1">加载失败</h3>
          <p className="text-sm text-red-700">
            {error instanceof Error ? error.message : "未知错误"}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm font-medium text-red-600 hover:text-red-700 underline"
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Queue Status Overview */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t.title}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="刷新"
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={handleResetStats}
              disabled={resetStatsMutation.isPending}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-1 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span>{t.resetStats}</span>
            </button>
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-500">{t.status}:</span>
          {stats?.is_running ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3" />
              {t.running}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              <XCircle className="w-3 h-3" />
              {t.stopped}
            </span>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Queue Size */}
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Inbox className="w-4 h-4 text-blue-500" />
              <p className="text-xs text-gray-500">{t.queueSize}</p>
            </div>
            <p className="text-2xl font-bold text-blue-600">
              {stats?.queue_size ?? 0}
            </p>
          </div>

          {/* Processed */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-gray-400" />
              <p className="text-xs text-gray-500">{t.processed}</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {stats?.total_processed?.toLocaleString() ?? 0}
            </p>
          </div>

          {/* Success */}
          <div className="bg-green-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <p className="text-xs text-gray-500">{t.success}</p>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {stats?.total_success?.toLocaleString() ?? 0}
            </p>
          </div>

          {/* Failed */}
          <div className="bg-red-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-red-500" />
              <p className="text-xs text-gray-500">{t.failed}</p>
            </div>
            <p className="text-2xl font-bold text-red-600">
              {stats?.total_failed?.toLocaleString() ?? 0}
            </p>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Merged */}
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">{t.merged}</span>
            <span className="text-sm font-medium text-gray-900">
              {stats?.total_merged?.toLocaleString() ?? 0}
            </span>
          </div>

          {/* Saved Calls */}
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">{t.savedCalls}</span>
            <span className="text-sm font-medium text-green-600">
              {stats?.saved_calls?.toLocaleString() ?? 0}
            </span>
          </div>

          {/* Merge Rate */}
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">{t.mergeRate}</span>
            <span className="text-sm font-medium text-blue-600">
              {formatPercent(stats?.merge_rate ?? 0)}
            </span>
          </div>

          {/* Cost Saving Rate */}
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">{t.costSaving}</span>
            <span className="text-sm font-medium text-green-600">
              {formatPercent(stats?.cost_saving_rate ?? 0)}
            </span>
          </div>
        </div>
      </Card>

      {/* Dead Letter Queue */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t.deadLetter}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{t.deadLetterDesc}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(stats?.dead_letter_size ?? 0) > 0 && (
              <>
                <button
                  onClick={handleRetryAll}
                  disabled={retryAllMutation.isPending}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
                >
                  {retryAllMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  <span>{t.retryAll}</span>
                </button>
                <button
                  onClick={handleClearAll}
                  disabled={clearMutation.isPending}
                  className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
                >
                  {clearMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  <span>{t.clearAll}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Dead Letter Size Badge */}
        <div className="flex items-center gap-2 mb-4">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              (stats?.dead_letter_size ?? 0) > 0
                ? "bg-amber-100 text-amber-800"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {stats?.dead_letter_size ?? 0} 个失败任务
          </span>
        </div>

        {/* Dead Letter Tasks List */}
        {(stats?.dead_letter_preview?.length ?? 0) > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.taskId}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.userId}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.memoryType}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.error}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.failedAt}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.retryCount}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats?.dead_letter_preview?.map(
                  (task: DeadLetterTask, index: number) => (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono text-gray-900 truncate max-w-[120px]">
                        {task.id.slice(0, 8)}...
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate max-w-[120px]">
                        {task.user_id.slice(0, 8)}...
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            task.memory_type === "user"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {task.memory_type}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3 text-sm text-red-600 truncate max-w-[200px]"
                        title={task.error}
                      >
                        {task.error}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(task.failed_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {task.retry_count}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleRetryTask(index)}
                          disabled={
                            retryTaskMutation.isPending &&
                            selectedTask === index
                          }
                          className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                        >
                          {retryTaskMutation.isPending &&
                          selectedTask === index ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            t.retry
                          )}
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            {(stats?.dead_letter_size ?? 0) >
              (stats?.dead_letter_preview?.length ?? 0) && (
              <div className="text-center py-3 text-sm text-gray-500">
                还有{" "}
                {(stats?.dead_letter_size ?? 0) -
                  (stats?.dead_letter_preview?.length ?? 0)}{" "}
                个任务未显示
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
            <p>{t.noTasks}</p>
          </div>
        )}
      </Card>

      {/* System Task Queue Section */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">{st.title}</h3>
          </div>
          <button
            onClick={() => refetchSystemTask()}
            disabled={isFetchingSystemTask}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="刷新"
          >
            <RefreshCw
              className={`w-4 h-4 ${
                isFetchingSystemTask ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>

        {isLoadingSystemTask ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <>
            {/* System Task Queue Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{st.queueSize}</p>
                <p className="text-2xl font-bold text-purple-600">
                  {systemTaskStats?.queue_size ?? 0}
                </p>
              </div>
              <div
                className={`rounded-lg p-3 ${
                  (systemTaskStats?.dead_letter_size ?? 0) > 0
                    ? "bg-amber-50"
                    : "bg-gray-50"
                }`}
              >
                <p className="text-xs text-gray-500 mb-1">
                  {st.deadLetterSize}
                </p>
                <p
                  className={`text-2xl font-bold ${
                    (systemTaskStats?.dead_letter_size ?? 0) > 0
                      ? "text-amber-600"
                      : "text-gray-600"
                  }`}
                >
                  {systemTaskStats?.dead_letter_size ?? 0}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{st.maxRetries}</p>
                <p className="text-lg font-semibold text-gray-700">
                  {systemTaskStats?.max_retries ?? 3}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{st.retryDelay}</p>
                <p className="text-lg font-semibold text-gray-700">
                  {systemTaskStats?.retry_initial_delay ?? 2}s
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{st.jobTimeout}</p>
                <p className="text-lg font-semibold text-gray-700">
                  {systemTaskStats?.job_timeout ?? 300}s
                </p>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* System Task Dead Letter Queue */}
      {(systemTaskStats?.dead_letter_size ?? 0) > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {st.deadLetter}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {st.deadLetterDesc}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRetryAllSystemTasks}
                disabled={retryAllSystemTasksMutation.isPending}
                className="px-3 py-1.5 text-sm bg-purple-600 text-white hover:bg-purple-700 rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
              >
                {retryAllSystemTasksMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span>{t.retryAll}</span>
              </button>
              <button
                onClick={handleClearAllSystemTasks}
                disabled={clearSystemTaskDeadLetterMutation.isPending}
                className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
              >
                {clearSystemTaskDeadLetterMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                <span>{t.clearAll}</span>
              </button>
            </div>
          </div>

          {/* System Task Dead Letter Tasks List */}
          {(systemTaskDeadLetter?.tasks?.length ?? 0) > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {st.taskType}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {st.scheduledBy}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {st.finalError}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {st.failedAt}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {st.retryCount}
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {systemTaskDeadLetter?.tasks?.map(
                    (task: SystemTaskDeadLetterItem, index: number) => (
                      <tr
                        key={`${task.task_type}-${task.enqueued_at}-${index}`}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {task.task_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {task.scheduled_by || "-"}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-red-600 truncate max-w-[200px]"
                          title={task.final_error || task.last_error || ""}
                        >
                          {task.final_error || task.last_error || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {task.failed_at
                              ? new Date(task.failed_at).toLocaleString()
                              : "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {task.retry_count}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleRetrySystemTask(index)}
                            disabled={
                              retrySystemTaskMutation.isPending &&
                              selectedSystemTask === index
                            }
                            className="px-2 py-1 text-xs text-purple-600 hover:bg-purple-50 rounded transition-colors disabled:opacity-50"
                          >
                            {retrySystemTaskMutation.isPending &&
                            selectedSystemTask === index ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              t.retry
                            )}
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
              <p>{st.noTasks}</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
